import * as t from 'io-ts';

import {makeEnumRuntimeType} from '../../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {RCardGroupIdRef, RCardIdRef} from '../../../../models/api/apiTypes';
import {RCustomFieldAttribute} from '../../../../models/api/customFieldAttributeModel';
import {RAsyncStatus} from '../../../appTypes';

/*
 * Form Field Runtime types.
 */

export enum CreateContactFormFieldValidityStatusesEnum {
  VALID = 'VALID',
  INVALID = 'INVALID',
  DUPLICATE = 'DUPLICATE',
  DUPLICATE_SELF = 'DUPLICATE_SELF',
  TO_BE_CHECKED = 'TO_BE_CHECKED',
}

export enum CreateContactFormFieldTypesEnum {
  EMAIL = 'EMAIL',
  PHONE_NUMBER = 'PHONE_NUMBER',
  TWITTER = 'TWITTER',
}

const RCreateContactFormBase = t.intersection([
  t.type({
    uid: t.string,
    value: t.string,
    type: makeEnumRuntimeType(CreateContactFormFieldTypesEnum),
  }),
  t.partial({
    validity: makeEnumRuntimeType(CreateContactFormFieldValidityStatusesEnum),
    duplicateCardId: RCardIdRef,
  }),
]);

const RCreateContactEmailFormFieldDirty = t.intersection([
  RCreateContactFormBase,
  t.type({
    type: t.literal(CreateContactFormFieldTypesEnum.EMAIL),
  }),
]);

const RCreateContactPhoneFormFieldDirty = t.intersection([
  RCreateContactFormBase,
  t.type({
    type: t.literal(CreateContactFormFieldTypesEnum.PHONE_NUMBER),
  }),
]);

const RCreateContactTwitterHandleFormFieldDirty = t.intersection([
  RCreateContactFormBase,
  t.type({
    type: t.literal(CreateContactFormFieldTypesEnum.TWITTER),
  }),
]);

const RCreateContactLinkFieldDirty = t.type({
  uid: t.string,
  value: t.string,
});

/*
 * Form Field Types.
 */

export interface CreateContactEmailFormFieldModel
  extends Readonly<t.TypeOf<typeof RCreateContactEmailFormFieldDirty>> {}
export const RCreateContactEmailFormField = t.clean<CreateContactEmailFormFieldModel>(
  RCreateContactEmailFormFieldDirty as any,
);

export interface CreateContactPhoneFormFieldModel
  extends Readonly<t.TypeOf<typeof RCreateContactPhoneFormFieldDirty>> {}
export const RCreateContactPhoneFormField = t.clean<CreateContactPhoneFormFieldModel>(
  RCreateContactPhoneFormFieldDirty as any,
);

export interface CreateContactTwitterHandleFormFieldModel
  extends Readonly<t.TypeOf<typeof RCreateContactTwitterHandleFormFieldDirty>> {}
export const RCreateContactTwitterHandleFormField = t.clean<CreateContactTwitterHandleFormFieldModel>(
  RCreateContactTwitterHandleFormFieldDirty as any,
);

export interface CreateContactLinkFieldModel
  extends Readonly<t.TypeOf<typeof RCreateContactLinkFieldDirty>> {}
export const RCreateContactLinkField = t.clean<CreateContactLinkFieldModel>(
  RCreateContactLinkFieldDirty as any,
);

const RCreateContactFormFieldDirty = t.taggedUnion('type', [
  RCreateContactEmailFormField,
  RCreateContactPhoneFormField,
  RCreateContactTwitterHandleFormField,
]);

export type CreateContactFormFieldModel =
  | CreateContactEmailFormFieldModel
  | CreateContactPhoneFormFieldModel
  | CreateContactTwitterHandleFormFieldModel;
export const RCreateContactFormField = t.clean<CreateContactFormFieldModel>(
  RCreateContactFormFieldDirty as any,
);

/*
 * Runtime types.
 */

export const RCreateContactFormStateDirty = t.type({
  name: t.string,
  description: t.string,
  avatarFile: t.unknown, // File type
  access: t.string,
  accountId: t.union([t.number, t.undefined]),
  contactFields: t.readonlyArray(RCreateContactFormField),
  customAttributes: t.readonlyArray(RCustomFieldAttribute),
  createStatus: t.union([RAsyncStatus, t.undefined]),
  cardGroupIds: t.readonlyArray(RCardGroupIdRef),
  links: t.readonlyArray(RCreateContactLinkField),
});

/*
 * Types.
 */

export interface CreateContactFormState extends Readonly<t.TypeOf<typeof RCreateContactFormStateDirty>> {}
export const RCreateContactFormState = t.clean<CreateContactFormState>(RCreateContactFormStateDirty as any);

/*
 * Type guards.
 */

export function createFormFieldIsEmailField(
  field?: CreateContactFormFieldModel,
): field is CreateContactEmailFormFieldModel {
  return field?.type === CreateContactFormFieldTypesEnum.EMAIL;
}

export function createFormFieldIsPhoneNumberField(
  field?: CreateContactFormFieldModel,
): field is CreateContactPhoneFormFieldModel {
  return field?.type === CreateContactFormFieldTypesEnum.PHONE_NUMBER;
}

export function createFormFieldIsTwitterHandleField(
  field?: CreateContactFormFieldModel,
): field is CreateContactTwitterHandleFormFieldModel {
  return field?.type === CreateContactFormFieldTypesEnum.TWITTER;
}
